import { Component, ElementRef, Inject, OnInit, ViewChild } from '@angular/core';
import { BzgModalRef, BZG_MODAL_DATA } from 'bz-genome-angular';
import { IKBCatalog, IKBArtifact, ArtifactStatus } from '../interfaces/IBase-knowledge-catalog';
import { KBCatalogFactory } from '../factories/kb-catalog.factory';
import { FileHandlerService, FileConversionResult } from '../services/file-handler.service';
import { ChangeTrackerService } from '../services/change-tracker.service';
import { NotificationService } from '../services/notification.service';
import { FileValidationService } from '../services/file-validation.service';
import { I18nService } from 'i18n/i18n.service';

@Component({
  selector: 'add-artifact-modal',
  templateUrl: './add-artifact-modal.html',
  styleUrls: ['./add-artifact-modal.scss']
})
export class AddArtifactModalComponent {
  @ViewChild('fileUpload') fileUploader: ElementRef;

  public catalog: IKBCatalog = { Id: '', IdParent: '', KBArtifacts: [], Description: '' , DisplayName: ''};
  private originalCatalog: IKBCatalog;

  constructor(
    public modalComponentRef: BzgModalRef, 
    @Inject(BZG_MODAL_DATA) public data: any,
    private _fileHandlerService: FileHandlerService,
    private _changeTrackerService: ChangeTrackerService,
    private _notificationService: NotificationService,
    private _fileValidationService: FileValidationService,
    private _i18nService: I18nService,
  ) { }

  ngOnInit() {
    if(!this.data) {
      this.catalog = KBCatalogFactory.createNew();
    } else {
      this.catalog = this.data;
    }
    
    this.originalCatalog = this.deepCloneCatalog(this.catalog);
  }

  close() {
    this.revertCatalogToOriginalState();
    this.modalComponentRef.close();
  }

  ok() {
    this.modalComponentRef.close(this.catalog);
  }

  getActiveArtifacts(): IKBArtifact[] {
    return this.catalog.KBArtifacts?.filter(artifact => !artifact.Deleted) || [];
  }

  getDeletedArtifacts(): IKBArtifact[] {
    return this.catalog.KBArtifacts?.filter(artifact => artifact.Deleted && artifact.Id) || [];
  }

  btnDisabled() {
    return this.hasEmptyRequiredFields() || 
           this.hasNoArtifacts() || 
           this.hasNoChangesToSave();
  }

  uploadFile() {
    this.fileUploader.nativeElement.click();
  }

  onFilesUploaded() {
    const files = this.fileUploader.nativeElement.files;
    if (files.length === 0) {
      return;
    }

    const fileArray = Array.from(files) as File[];
    const validationResult = this._fileValidationService.validateUploadedFiles(
      fileArray, 
      this.catalog.KBArtifacts || []
    );
    
    // Verificar si hay error de máximo de archivos
    if (validationResult.hasMaxFilesError && validationResult.maxFilesErrorMessage) {
      this._notificationService.showMaxFilesExceededError(validationResult.maxFilesErrorMessage);
      this.clearFileInput();
      return;
    }
    
    // Mostrar errores de archivos individuales si los hay
    if (validationResult.invalidFiles.length > 0) {
      this._notificationService.showInvalidFilesError(validationResult.invalidFiles);
    }
    
    if (validationResult.validFiles.length === 0) {
      this.handleNoValidFiles();
      return;
    }
    
    this.processValidFiles(validationResult.validFiles);
    this.clearFileInput();
  }

  removeFile(index: number): void {
    if (this.catalog.KBArtifacts && index >= 0 && index < this.catalog.KBArtifacts.length) {
      this.catalog.KBArtifacts[index].Deleted = true;
      this._changeTrackerService.markCatalogAsChanged(this.catalog);
    }
  }

  onArtifactChanged(artifact: IKBArtifact): void {
    this._changeTrackerService.markCatalogAsChanged(this.catalog);
  }

  onCatalogFieldChange(): void {
    this._changeTrackerService.markCatalogAsChanged(this.catalog);
  }

  private handleConvertedFiles(results: FileConversionResult[]): void {
    results.forEach(result => {
      
      if (!this.catalog.KBArtifacts) {
        this.catalog.KBArtifacts = [];
      }

      const newArtifact = {
        FileName: result.fileName,
        Content: result.base64Content,
        DisplayName: this._fileHandlerService.removeFileExtension(result.fileName),
        Description: '',
        Extension: this._fileHandlerService.getFileExtension(result.fileName),
        HasChanges: true,
        Deleted: false,
        ArtifactStatus: ArtifactStatus.ToProcess,
        ArtifactStatusLocalized: this._i18nService.instant('BASEKNOWLEDGECATALOG.ARTIFACT_STATUS.' + ArtifactStatus.ToProcess.toLocaleUpperCase())
      };
      
      this.catalog.KBArtifacts.push(newArtifact);
    });
    
    this._changeTrackerService.markCatalogAsChanged(this.catalog);
  }

  private hasEmptyRequiredFields(): boolean {
    return this.isFieldEmpty(this.catalog.DisplayName) || 
           this.isFieldEmpty(this.catalog.Description);
  }

  private hasNoArtifacts(): boolean {
    return !this.catalog.KBArtifacts || 
           this.catalog.KBArtifacts.length === 0 ||
           this.catalog.KBArtifacts.every(artifact => artifact.Deleted);
  }

  private hasNoChangesToSave(): boolean {
    return !this._changeTrackerService.hasCatalogChanges(this.catalog) && 
           this._changeTrackerService.getChangedArtifacts(this.catalog).length === 0;
  }

  private isFieldEmpty(field: string | undefined | null): boolean {
    return !field || field.trim().length === 0;
  }

  private handleNoValidFiles(): void {
    this.clearFileInput();
  }

  private processValidFiles(validFiles: File[]): void {
    this._fileHandlerService.convertFilesToBase64(validFiles).subscribe({
      next: (results) => {
        this.handleConvertedFiles(results);
      },
      error: (error) => {
        this._notificationService.showFileConversionError(error);
      }
    });
  }

  private clearFileInput(): void {
    this.fileUploader.nativeElement.value = '';
  }

  private deepCloneCatalog(catalog: IKBCatalog): IKBCatalog {
    return {
      Id: catalog.Id,
      IdParent: catalog.IdParent,
      DisplayName: catalog.DisplayName,
      Description: catalog.Description,
      KBArtifacts: catalog.KBArtifacts ? catalog.KBArtifacts.map(artifact => ({
        FileName: artifact.FileName,
        Content: artifact.Content,
        DisplayName: artifact.DisplayName,
        Description: artifact.Description,
        Extension: artifact.Extension,
        HasChanges: artifact.HasChanges || false,
        ArtifactStatus: artifact.ArtifactStatus,
        ArtifactStatusLocalized: artifact.ArtifactStatusLocalized || '',
        deleted: artifact.Deleted || false,
      })) : []
    };
  }

  private revertCatalogToOriginalState(): void {
    if (this.originalCatalog) {
      this.catalog.Id = this.originalCatalog.Id;
      this.catalog.IdParent = this.originalCatalog.IdParent;
      this.catalog.DisplayName = this.originalCatalog.DisplayName;
      this.catalog.Description = this.originalCatalog.Description;
      this.catalog.KBArtifacts = this.deepCloneCatalog(this.originalCatalog).KBArtifacts;
    }
  }

}
